import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from '@mui/material';
import Logo from '../../../../assets/images/logo/vms-pro-logo.svg';
import { t } from 'i18next';

// Helper formatters
const formatAddress = (obj) => [obj?.street, obj?.city, obj?.country].filter(Boolean).join(', ');
const vehicleDescription = (v) => [v?.vehicleType?.name, v?.year, v?.make?.name, v?.model?.name].filter(Boolean).join(' ');
const formatCurrency = (val) =>
  val || val === 0
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val)
    : '-';

const ShipmentPrintPreview = ({ open, onClose, row }) => {
  const printRef = useRef(null);
  if (!row) return null;
  const booking = row.booking || {};
  const containers = row.containers || [];
  const exporter = row?.exporter || row?.client?.exporter;
  const consignee = row?.consignee;
  const notifyParty = row?.notifyParty;
  const consigneeDock = row?.consigneeDock;

  const totalVehicles = containers.reduce((s, c) => s + (c.vehicles?.length || 0), 0);
  const totalNet = containers.reduce((s, c) => s + (c.netWeight || 0), 0);
  const totalPriceAll = containers.reduce(
    (s, c) =>
      s + (c.vehicles || []).reduce((sv, v) => sv + (Number(v.price ?? v.unitPrice) || 0), 0),
    0
  );

  const handlePrint = () => {
    // Simple approach: rely on global window.print
    window.print();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>{t('Shipment Preview')} #{row.id}</Box>
        <Box className="no-print" sx={{ fontSize: 12, color: 'text.secondary' }}>{t('Generated At')}: {new Date().toLocaleString()}</Box>
      </DialogTitle>
  <DialogContent dividers sx={{ bgcolor: '#FAFBFC' }}>
        {/* Print styles */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; inset: 0; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-page-break { page-break-after: always; }
            .table-header { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .section-title { background:#0B3C5D !important; color:#fff !important; padding:4px 8px; border-radius:4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>
        <Box ref={printRef} className="print-area" sx={{ p: 2 }}>
          {/* Header with Logo */}
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, pb:1, borderBottom:'2px solid #0B3C5D' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Box component="img" src={Logo} alt="VMS PRO" sx={{ height:48, width:'auto', display:'block' }} />
              <Typography variant="caption" sx={{ color:'text.secondary' }}>{t('Shipment Document')}</Typography>
            </Box>
            <Typography variant="caption" sx={{ fontStyle:'italic', color:'text.secondary' }}>Powered by Pinetechs.com</Typography>
          </Box>
          {/* Summary Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" className="section-title" sx={{ mb: 2, display: 'inline-block' }}>{t('Booking Summary')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" label={`${t('Booking Number')}: ${booking.number || '-'}`} />
              <Chip size="small" label={`${t('Status')}: ${booking?.bookingStatus?.name || '-'}`} />
              <Chip size="small" label={`${t('Destination')}: ${booking?.destination?.name || '-'}`} />
              <Chip size="small" label={`${t('Shipping Line')}: ${booking?.shippingLine?.name || '-'}`} />
              <Chip size="small" label={`${t('Vessel')}: ${booking.vesselName || '-'}`} />
              <Chip size="small" label={`${t('Voyage')}: ${booking.voyageNumber || '-'}`} />
              <Chip size="small" color="info" label={`${t('Containers')}: ${containers.length}`} />
              <Chip size="small" color="success" label={`${t('Vehicles')}: ${totalVehicles}`} />
              <Chip size="small" color="secondary" label={`${t('Net Wt')}: ${totalNet}`} />
              {totalPriceAll > 0 && <Chip size="small" color="warning" label={`${t('Total Value')}: ${formatCurrency(totalPriceAll)}`} />}
            </Box>
            <Divider sx={{ mt: 2 }} />
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
              <Typography variant="caption"><strong>{t('Entry Date')}:</strong> {row.entryDate || '-'}</Typography>
              <Typography variant="caption"><strong>{t('Earliest Receiving Date')}:</strong> {booking.earliestReceivingDate || '-'}</Typography>
              <Typography variant="caption"><strong>{t('Latest Return Date')}:</strong> {booking.latestReturnDate || '-'}</Typography>
              <Typography variant="caption"><strong>{t('DOC Cutoff Date')}:</strong> {booking.cutoffDate || '-'}</Typography>
              <Typography variant="caption"><strong>{t('Estimated Sailing Date')}:</strong> {booking.estimatedSailingDate || '-'}</Typography>
              <Typography variant="caption"><strong>{t('Estimated Arrival Date')}:</strong> {booking.estimatedArrivalDate || '-'}</Typography>
            </Stack>
          </Box>
          {containers.map((c, ci) => {
       
            const containerPriceTotal = (c.vehicles || []).reduce((sv, v) => sv + (Number(v.price ?? v.unitPrice) || 0), 0);
            const containerHasPrice = containerPriceTotal > 0;
            return (
              <Box key={c.id || ci} sx={{ mb: 4, pageBreakInside: 'avoid' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{t('Container')} #{ci + 1}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  {c.containerNumber && <Chip size="small" label={`${t('Number')}: ${c.containerNumber}`} />}
                  {c.sealNumber && <Chip size="small" label={`${t('Seal')}: ${c.sealNumber}`} />}
                  {c.containerSize && <Chip size="small" label={`${t('Size')}: ${c.containerSize}`} />}
                  <Chip size="small" color="info" label={`${t('Net Weight')}: ${c.netWeight ?? 0}`} />
                  <Chip size="small" label={`${t('Parts')}: ${c.partsInContainer ? t('Yes') : t('No')}`} />
                  {c?.client?.name && <Chip size="small" label={`${t('Client')}: ${c.client.name}`} />}
                  {containerHasPrice && <Chip size="small" color="warning" label={`${t('Value')}: ${formatCurrency(containerPriceTotal)}`} />}
                </Stack>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, mb: 2 }}>
                  {exporter && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Exporter')}</Typography>
                      <Typography variant="body2">{exporter.name}</Typography>
                      <Typography variant="caption" display="block"><strong>{t('Export #')}:</strong> {exporter.exportNumber || '-'}</Typography>
                      {exporter.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {exporter.phoneNumber}</Typography>}
                      {exporter.email && <Typography variant="caption" display="block">{t('Email')}: {exporter.email}</Typography>}
                      <Typography variant="caption" color="text.secondary" display="block">{formatAddress(exporter)}</Typography>
                    </Box>
                  )}
                  {consignee && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Consignee')}</Typography>
                      <Typography variant="body2">{consignee.name}</Typography>
                      <Typography variant="caption" display="block"><strong>{t('Consignee #')}:</strong> {consignee.consigneeNumber || '-'}</Typography>
                      {consignee.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {consignee.phoneNumber}</Typography>}
                      {consignee.email && <Typography variant="caption" display="block">{t('Email')}: {consignee.email}</Typography>}
                      <Typography variant="caption" color="text.secondary" display="block">{formatAddress(consignee)}</Typography>
                    </Box>
                  )}

                  {notifyParty && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Notify Party')}</Typography>
                      <Typography variant="body2">{notifyParty.name}</Typography>
                      <Typography variant="caption" display="block"><strong>{t('Notify Party #')}:</strong> {notifyParty.notifyPartyNumber || '-'}</Typography>
                      {notifyParty.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {notifyParty.phoneNumber}</Typography>}
                      {notifyParty.email && <Typography variant="caption" display="block">{t('Email')}: {notifyParty.email}</Typography>}
                      <Typography variant="caption" color="text.secondary" display="block">{formatAddress(notifyParty)}</Typography>
                    </Box>
                  )}

{consigneeDock && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Consignee Dock')}</Typography>
                      <Typography variant="body2">{consigneeDock.name}</Typography>
                      <Typography variant="caption" display="block"><strong>{t('Consignee Dock #')}:</strong> {consigneeDock.consigneeDockNumber || '-'}</Typography>
                      {consigneeDock.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {consigneeDock.phoneNumber}</Typography>}
                      {consigneeDock.email && <Typography variant="caption" display="block">{t('Email')}: {consigneeDock.email}</Typography>}
                      <Typography variant="caption" color="text.secondary" display="block">{formatAddress(consigneeDock)}</Typography>
                    </Box>
                  )}




                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Vehicles')}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('Total')}: {c.vehicles?.length || 0}</Typography>
                  </Box>
                </Box>
                {c.vehicles?.length > 0 && (
                  <Table size="small" sx={{ mb: 2, '& th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow className="table-header">
                        <TableCell>{t('ID')}</TableCell>
                        <TableCell>{t('Description')}</TableCell>
                        <TableCell>{t('VIN')}</TableCell>
                        <TableCell>{t('Weight')}</TableCell>
                        <TableCell>{t('Fuel Type')}</TableCell>
                        {containerHasPrice && <TableCell>{t('Price')}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {c.vehicles.map((v, vi) => {
                        const price = Number(v.price ?? v.unitPrice) || 0;
                        return (
                          <TableRow key={v.id || vi}>
                            <TableCell>{v.id}</TableCell>
                            <TableCell>{vehicleDescription(v)}</TableCell>
                            <TableCell>{v.vin}</TableCell>
                            <TableCell>{v.weight}</TableCell>
                            <TableCell>{v.fuelType?.name}</TableCell>
                            {containerHasPrice && <TableCell>{price ? formatCurrency(price) : '-'}</TableCell>}
                          </TableRow>
                        );
                      })}
                      {containerHasPrice && (
                        <TableRow>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>{t('Total')}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(containerPriceTotal)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
                <Divider />
              </Box>
            );
          })}
          {/* Footer branding */}
          <Box sx={{ mt:4, pt:1, borderTop:'1px dashed #b0bec5', textAlign:'center' }} className="no-print print-brand-footer">
            <Typography variant="caption" sx={{ color:'text.secondary' }}>VMS PRO • Powered by Pinetechs.com</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="no-print" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">{t('Total Vehicles')}: {totalVehicles} | {t('Total Net Weight')}: {totalNet} {totalPriceAll > 0 && `| ${t('Total Value')}: ${formatCurrency(totalPriceAll)}`}</Typography>
        <Box>
          <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>{t('Close')}</Button>
          <Button onClick={handlePrint} variant="contained">{t('Print')}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

ShipmentPrintPreview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
};

export default ShipmentPrintPreview;
